import React, { useState } from "react";
import {
    Box,
    Grid,
    Paper,
    Typography,
    IconButton,
    Collapse,
} from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ExpandCircleDownOutlinedIcon from "@mui/icons-material/ExpandCircleDownOutlined";

export type InstructionsData = {
    heading: string;
    data: {
        icon: JSX.Element;
        title: string;
        description: JSX.Element;
    }[];
}

interface UploadInstructionProps {
    cardData: InstructionsData 
}

const FileUploadInstructionComponent: React.FC<UploadInstructionProps> = ({cardData}) => {
    const [open, setOpen] = useState(true);

    return (
        <>
            <Box className='mb-6 rounded' sx={{ backgroundColor: '#EFF3F6', padding: '15px', borderRadius: '12px' }}>
                {/* Header */}
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center" gap={1}>
                        <div
                            style={{
                                width: 40,
                                height: 40,
                                borderRadius: "50%",
                                backgroundColor: "#DAE3E2",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <WarningAmberRoundedIcon sx={{ color: "#0F6157", fontSize: 28, marginTop: '-2px' }} />
                        </div>
                        <Typography
                            fontSize={'16px'}
                            fontWeight={600}
                            sx={{ color: "#013238" }}
                        >
                            {cardData?.heading}
                        </Typography>
                    </Box>

                    <IconButton onClick={() => setOpen(!open)}>
                        {open ? <ExpandCircleDownOutlinedIcon sx={{ transform: 'rotate(180deg)', fontSize: '24px' }} /> : <ExpandCircleDownOutlinedIcon sx={{ fontSize: '24px' }} />}
                    </IconButton>
                </Box>

                {/* Collapsible Body */}
                <Collapse in={open} timeout={300}>
                    <Box mt={2}>
                        <Grid container spacing={2}>

                            {/* LEFT COLUMN: Card 1 only */}
                            <Grid item xs={12} sm={12} md={4}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 2.5,
                                        height: "100%",
                                        borderRadius: "12px",
                                        border: "1px solid #E4E7E7",
                                        backgroundColor: "#FFFFFF",
                                    }}
                                >
                                    <Box display="flex" flexDirection="column" alignItems="flex-start" gap={2}>
                                        <Box display="flex" alignItems="flex-start" gap={1} flexDirection={'column'}>
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="center"
                                                borderRadius={1}
                                                className="p-1"
                                                width={'36px'}
                                                height={'36px'}
                                                sx={{ backgroundColor: "#DBE2E3" }}
                                            >
                                                {cardData?.data[0].icon}
                                            </Box>
                                            <Typography fontSize={'14px'} fontWeight={500} color="#191919" sx={{ paddingTop: "8px" }}>
                                                {cardData?.data[0].title}
                                            </Typography>
                                        </Box>

                                        <Typography fontSize={'12px'} fontWeight={400} color="#191919">
                                            {cardData?.data[0].description}
                                        </Typography>
                                    </Box>
                                </Paper>
                            </Grid>

                            {/* RIGHT COLUMN: Cards 2, 3, 4, 5 in 2×2 layout */}
                            <Grid item xs={12} md={8}>
                                <Grid container spacing={2}>
                                    {cardData?.data?.slice(1).map((card: any, index: any) => (
                                        <Grid item xs={12} sm={6} key={index}>
                                            <Paper
                                                elevation={0}
                                                sx={{
                                                    p: 2.5,
                                                    height: "100%",
                                                    borderRadius: "12px",
                                                    border: "1px solid #E4E7E7",
                                                    backgroundColor: "#FFFFFF",
                                                }}
                                            >
                                                <Box display="flex" flexDirection="column" alignItems="flex-start" gap={2}>
                                                    <Box display="flex" alignItems="flex-start" gap={1} flexDirection={'column'}>
                                                        <Box
                                                            display="flex"
                                                            alignItems="center"
                                                            justifyContent="center"
                                                            borderRadius={1}
                                                            minWidth={'36px'}
                                                            minHeight={'36px'}
                                                            flexShrink={0}
                                                            sx={{ backgroundColor: "#DBE3E2" }}
                                                        >
                                                            {card?.icon}
                                                        </Box>
                                                        <Typography
                                                            fontSize={'14px'}
                                                            fontWeight={500}
                                                            color="#191919"
                                                            sx={{lineHeight: 1.3}}
                                                        >
                                                            {card?.title}
                                                        </Typography>
                                                    </Box>

                                                    <Typography fontSize={'12px'} fontWeight={400} color="#191919" sx={{wordBreak: 'break-word'}}>
                                                        {card?.description}
                                                    </Typography>
                                                </Box>
                                            </Paper>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Grid>
                        </Grid>
                    </Box>
                </Collapse>
            </Box>
        </>
    );
}

export default FileUploadInstructionComponent